<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref } from 'vue';

const props = defineProps({
  activator: { type: HTMLElement, required: true },
  text: { type: String, required: true },
  position: { type: String, default: 'bottom' },
  disabled: { type: Boolean },
  nudgeRight: { type: Number, default: 0 },
  openDelay: { type: Number, default: 100 },
});

// A trigger that opens a menu leaves the pointer in place, so a hover tooltip survives on top
// of what it just opened. Suppression holds until the pointer leaves or focus moves out of the
// trigger rather than closing once, because a menu has no scrim and the trigger stays hoverable.
// Capture phase: the tooltip often sits on a wrapper around the real activator, whose click
// handler calls stopPropagation().
const suppressEvents = ['click', 'contextmenu'];
const releaseEvents = ['mouseleave', 'focusout'];

const suppressed = ref(false);

function suppress() {
  suppressed.value = true;
}

function release() {
  suppressed.value = false;
}

const inactive = computed(() => props.disabled || !props.text || suppressed.value);

onMounted(() => {
  for (const event of suppressEvents) props.activator.addEventListener(event, suppress, true);
  for (const event of releaseEvents) props.activator.addEventListener(event, release, true);
});

onBeforeUnmount(() => {
  for (const event of suppressEvents) props.activator.removeEventListener(event, suppress, true);
  for (const event of releaseEvents) props.activator.removeEventListener(event, release, true);
});
</script>

<template>
  <VTooltip
    :key="String(inactive)"
    :top="position === 'top'"
    :right="position === 'right'"
    :bottom="position === 'bottom'"
    :left="position === 'left'"
    :activator="activator"
    :disabled="inactive"
    :nudge-right="nudgeRight"
    :open-delay="openDelay"
  >
    <span v-text="text" />
  </VTooltip>
</template>
